/** Booking batch edit — the Bookings grid `edit_fields` batch op.
 *
 *  - `GET /bookings/batch/edit_fields/catalog/` — field registry (widgets, ops,
 *    tenant scope, follows-lead, declared cache/sync effects, sync-revert notes),
 *    effect catalogue and run options. The dialog renders from it.
 *  - `POST /bookings/batch/` with `action: 'edit_fields'` — dispatch (via
 *    `runBatch`); params `BookingEditParams`. Dry run optional.
 *  - `GET /bgtasks/{id}/result/` — `BookingEditTaskResult`.
 *
 *  Hand-authored (console convention); mirrors `BookingEdit*` in
 *  console.openapi.yaml (contract test: bookingEdit.contract.test.ts).
 */
import { apiGet } from './client';
import type { TourOperatorEnvironment } from './tourOperators';

export type BookingEditWidget =
  | 'tour'
  | 'role'
  | 'date'
  | 'time'
  | 'datetime'
  | 'enum'
  | 'text'
  | 'integer'
  | 'boolean';

export type BookingEditOp = 'set' | 'shift';

export interface BookingEditCatalogField {
  key: string;
  label: string;
  widget: BookingEditWidget;
  ops: BookingEditOp[];
  choices: Array<{ value: number; label: string }>;
  nullable: boolean;
  max_length: number | null;
  min_value: number | null;
  max_value: number | null;
  /** Applies only to bookings of the picked value's tenant. */
  tenant_scoped: boolean;
  /** Mirrored from a lead booking onto its sub-bookings. */
  follows_lead: boolean;
  effects: string[];
  automatic: string[];
  sync: 'reverts' | 'partial' | 'keeps';
  sync_note: string;
  help: string;
  warning: string;
  shift_unit: 'days' | 'minutes' | null;
}

export interface BookingEditCatalogEffect {
  code: string;
  label: string;
  description: string;
  kind: 'flushed' | 'automatic';
}

export interface BookingEditCatalog {
  enabled: boolean;
  max_selection: number;
  min_reason: number;
  fields: BookingEditCatalogField[];
  effects: BookingEditCatalogEffect[];
  options: {
    update_events: string[];
    cancel_modes: Array<{ value: CancelMode; label: string }>;
    cancel_mode_default: CancelMode;
    custom_end: string[];
    old_tour_tags: string[];
    order_link: string[];
  };
  event_tag_template: string;
}

export type CancelMode = 'cancel_with_event' | 'cancel_without_event' | 'silent_state';
export type UpdateEvents = 'emit' | 'suppress';

export interface BookingEditBlockWire {
  field: string;
  op?: BookingEditOp;
  value: unknown;
}

export interface BookingEditParams {
  dry_run: boolean;
  reason: string;
  events: { update_events: UpdateEvents; cancel_mode?: CancelMode };
  options: { custom_end?: 'move' | 'keep'; old_tour_tags?: 'drop' | 'keep'; order_link?: 'move' | 'keep' };
  blocks: BookingEditBlockWire[];
}

// ---------------------------------------------------------------------------
// Result
// ---------------------------------------------------------------------------

export type BookingEditOutcome =
  | 'would_change'
  | 'applied'
  | 'no_change'
  | 'missing'
  | 'conflict_duplicate'
  | 'rejected_value'
  | 'failed';

export type BookingEditBlockOutcome =
  | 'change'
  | 'no_change'
  | 'skipped_other_tenant'
  | 'skipped_bespoke'
  | 'skipped_sub_booking'
  | 'via_lead'
  | 'rejected_value';

export interface BookingEditBlockResult {
  field: string;
  outcome: BookingEditBlockOutcome;
  from?: unknown;
  to?: unknown;
  note?: string;
}

export interface BookingEditEventRecord {
  code: string;
  status: string;
  tag?: string;
  logid?: string | null;
  event_delivery_uuid?: string | null;
}

export interface BookingEditRecord {
  id: number;
  custom_id?: string | null;
  tenant?: { id: number; code: string };
  via_lead_of?: number | null;
  outcome: BookingEditOutcome;
  blocks: BookingEditBlockResult[];
  tags_removed?: string[];
  warnings: string[];
  effects: string[];
  events: BookingEditEventRecord[];
  detail?: string | null;
}

export interface BookingEditEffectSummary {
  label: string;
  kind: 'flushed' | 'automatic';
  count: number;
  keys?: unknown[];
  /** Record editor only: one human label per key ("Stonehenge & Bath · Thu 2 Oct 09:30
   *  Europe/London" for a check-in manifest). */
  key_labels?: string[];
  truncated?: boolean;
}

export interface BookingEditTaskResult {
  dry_run: boolean;
  stopped_early?: boolean;
  run_tag: string;
  params?: Record<string, unknown>;
  total: number;
  counts: Partial<Record<BookingEditOutcome, number>>;
  applied?: number;
  effects: Record<string, BookingEditEffectSummary>;
  flush?: Record<string, { ok: number; failed: number }>;
  history_failures?: number;
  records: BookingEditRecord[];
  records_truncated?: boolean;
}

export const OUTCOME_LABELS: Record<BookingEditOutcome, string> = {
  would_change: 'Will change',
  applied: 'Changed',
  no_change: 'No change',
  missing: 'No longer exists',
  conflict_duplicate: 'Would duplicate a booking',
  rejected_value: 'Value rejected',
  failed: 'Failed',
};

export const OUTCOME_SEVERITY: Record<BookingEditOutcome, 'success' | 'info' | 'warning' | 'error'> = {
  would_change: 'success',
  applied: 'success',
  no_change: 'info',
  missing: 'warning',
  conflict_duplicate: 'error',
  rejected_value: 'error',
  failed: 'error',
};

export const BLOCK_OUTCOME_LABELS: Record<BookingEditBlockOutcome, string> = {
  change: 'change',
  no_change: 'unchanged',
  skipped_other_tenant: 'skipped — other tenant',
  skipped_bespoke: 'skipped — bespoke tour',
  skipped_sub_booking: 'skipped — sub-booking without its lead',
  via_lead: 'via the lead booking',
  rejected_value: 'rejected',
};

export const EVENT_STATUS_LABELS: Record<string, string> = {
  would_emit: 'would emit',
  suppressed: 'suppressed',
  dropped_cancellation: 'not sent (cancellation)',
  not_sent_transition: 'not sent (state transition)',
  not_sent_trip_ended: 'not sent (trip ended)',
  via_lead_cancellation: 'via the lead’s cancellation',
  emitted: 'emitted',
  not_emitted: 'not emitted (no change / events off)',
  requested: 'requested',
  failed: 'failed',
};

export function fetchBookingEditCatalog(): Promise<BookingEditCatalog> {
  return apiGet<BookingEditCatalog>('/bookings/batch/edit_fields/catalog/');
}

// ---------------------------------------------------------------------------
// Pickers (tenant + pk rule: rows carry `tenant.id/code`)
// ---------------------------------------------------------------------------

export interface TenantRef {
  id: number;
  code: string;
  name: string;
  /** `Tour_Operator.kind` — the Tenant cell marks a sandbox (plan D8). */
  environment?: TourOperatorEnvironment | null;
}

export interface BookingEditTourOption {
  id: number;
  name: string;
  tenant: TenantRef | null;
  custom_id?: string | null;
  is_bespoke?: boolean;
  start_timezone?: string | null;
  start_date?: string | null;
}

export interface BookingEditRoleOption {
  id: number;
  name: string;
  tenant: TenantRef | null;
}

function qs(params: Record<string, string | number | null | undefined>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') usp.set(k, String(v));
  }
  return usp.toString();
}

/** Tours for the Tour block. Scoped to `tenantId` when the grid has a tenant
 *  filter; otherwise an unscoped search (needs a typed query). */
export async function tourPickerOptions(
  q: string,
  tenantId: number | null,
  signal?: AbortSignal,
): Promise<BookingEditTourOption[]> {
  const res = await apiGet<{ results: BookingEditTourOption[] }>(
    `/bookings/autocomplete/tours/?${qs({ q, tour_operator_id: tenantId, limit: 20 })}`,
    signal ? { signal } : undefined,
  );
  return res.results;
}

export async function rolePickerOptions(
  q: string,
  tenantId: number | null,
  signal?: AbortSignal,
): Promise<BookingEditRoleOption[]> {
  const res = await apiGet<{ results: BookingEditRoleOption[] }>(
    `/bookings/autocomplete/roles/?${qs({ q, tour_operator_id: tenantId, limit: 20 })}`,
    signal ? { signal } : undefined,
  );
  return res.results;
}

/** Delivery Logs deep link for a run's event tag. */
export function deliveryLogsHrefForTag(tag: string): string {
  return `/delivery-logs?q=${encodeURIComponent(`tag="${tag}"`)}`;
}
